import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ApiService } from '@shared/services/api.service';
import { zipWith } from 'lodash';
import { forkJoin, Observable } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';

type TimezoneList = { timezone: string; translation: string; offset: string }[];

/** ? */
@Injectable()
export class TimeZoneService {
  constructor(private readonly translate: TranslateService, private readonly api: ApiService) {}

  public get timezonesForSchedules(): Observable<TimezoneList> {
    return this.parseTimezonesFromResponse('/platform/api/v2/telephony/providers/edges/timezones');
  }

  public get timezoneList(): Observable<TimezoneList> {
    return this.parseTimezonesFromResponse('/platform/api/v2/timeZones?pageSize=1000');
  }

  public get timezonesWithOffsets(): Observable<string[]> {
    return this.timezoneList.pipe(
      map((timezones) => timezones.map((tz) => `${tz.translation} (${tz.offset})`)),
      shareReplay(1)
    );
  }

  private parseTimezonesFromResponse(url: string): Observable<TimezoneList> {
    let keys: string[];
    let offsets: string[];

    return this.api.get(url).pipe(
      switchMap((response) => {
        keys = (response.entities || []).map((e: any) => e.id);
        offsets = (response.entities || []).map((tz: any) => {
          const offset = tz.offset;
          let minutes = String(Math.abs(offset % 60));
          minutes += '0';

          return `${offset - Math.abs(offset) ? '-' : '+'}${Math.abs(
            Math.floor(offset / 60)
          )}:${minutes}`;
        });

        return forkJoin(keys.map((tzKey) => this.translate.get(tzKey)));
      }),
      map((tzTranslations: string[]) =>
        // Return a list with translations, keys AND offsets computed once for use in many formats
        zipWith(keys, tzTranslations, offsets, (translation, timezone, offset) => ({
          translation,
          timezone,
          offset
        }))
      ),
      shareReplay(1)
    );
  }
}
